import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { classNames } from "@/lib/format";
import { GuestTabletWorkstationsPanel } from "@/pages/stammdaten/GuestTabletWorkstationsPanel";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { fetchGuestTabletContent, updateGuestTabletContent } from "@/lib/guestTabletApi";
import { useCurrentUser } from "@/lib/auth";
import { compressImageFileToDataUrl } from "@/lib/imageUpload";
import { canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import { TabletIdleScreen } from "@/pages/guest-tablet/components/TabletIdleScreen";
import { useBranding } from "@/lib/branding/useBranding";
import "@/pages/guest-tablet/guest-tablet.css";
import type {
  GuestTabletContent,
  GuestTabletIdleImage,
  GuestTabletIdleTextBlock,
} from "@/types/guestTablet";

function newId(): string {
  return crypto.randomUUID();
}

const VARIANT_OPTIONS: { value: GuestTabletIdleTextBlock["variant"]; label: string }[] = [
  { value: "headline", label: "Überschrift" },
  { value: "body", label: "Fließtext" },
  { value: "highlight", label: "Hervorhebung" },
  { value: "hint", label: "Hinweis" },
];

type GuestTabletTab = "idle" | "workstations";

export function GuestTabletIdlePage() {
  const { siteName, logoUrl } = useBranding();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: GuestTabletTab =
    searchParams.get("tab") === "workstations" ? "workstations" : "idle";
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteStammdaten(groups);

  const [content, setContent] = useState<GuestTabletContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchGuestTabletContent();
      setContent(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageMeta({
      title: "Gäste-Tablet",
      subtitle:
        tab === "workstations"
          ? "Anzahl und Namen der Rezeptions-Tablets"
          : "Bilder, Texte und Diashow für ungenutzte Tablets an der Rezeption",
    });
  }, [setPageMeta, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const idle = content?.idle_screen;

  function patchIdle(patch: Partial<NonNullable<typeof idle>>) {
    if (!content || !idle) return;
    setContent({
      ...content,
      idle_screen: { ...idle, ...patch },
    });
    setSuccess(false);
  }

  async function onImageSelected(file: File | null) {
    if (!file || !canEdit || !idle) return;
    if (!file.type.startsWith("image/")) {
      setError("Bitte wählen Sie eine Bilddatei (PNG, JPEG oder WebP).");
      return;
    }
    try {
      const imageData = await compressImageFileToDataUrl(file);
      const next: GuestTabletIdleImage = {
        id: newId(),
        image_url: imageData,
        alt_text: file.name.replace(/\.[^.]+$/, ""),
        caption: null,
        sort_order: idle.images.length * 10 + 10,
      };
      patchIdle({ images: [...idle.images, next] });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bild konnte nicht geladen werden.");
    }
  }

  function updateTextBlock(id: string, patch: Partial<GuestTabletIdleTextBlock>) {
    if (!idle) return;
    patchIdle({
      text_blocks: idle.text_blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    });
  }

  function addTextBlock() {
    if (!idle) return;
    const block: GuestTabletIdleTextBlock = {
      id: newId(),
      title: null,
      body: "",
      variant: "body",
      sort_order: idle.text_blocks.length * 10 + 10,
    };
    patchIdle({ text_blocks: [...idle.text_blocks, block] });
  }

  function removeTextBlock(id: string) {
    if (!idle) return;
    patchIdle({ text_blocks: idle.text_blocks.filter((b) => b.id !== id) });
  }

  function removeImage(id: string) {
    if (!idle) return;
    patchIdle({ images: idle.images.filter((i) => i.id !== id) });
  }

  async function onSave() {
    if (!content || !canEdit) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const saved = await updateGuestTabletContent({ idle_screen: content.idle_screen });
      setContent(saved);
      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  if (tab === "workstations") {
    return (
      <div className="space-y-4">
        <GuestTabletTabBar tab={tab} onTab={(next) => setSearchParams(next === "idle" ? {} : { tab: next })} />
        <GuestTabletWorkstationsPanel />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <GuestTabletTabBar tab={tab} onTab={(next) => setSearchParams(next === "idle" ? {} : { tab: next })} />
        <p className="text-sm text-label-secondary">Leerlaufanzeige wird geladen…</p>
      </div>
    );
  }

  if (!idle) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-red-600">
          {error ?? "Die Leerlaufanzeige konnte nicht geladen werden."}
        </p>
        <Button type="button" variant="secondary" size="sm" onClick={() => void load()}>
          Erneut versuchen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GuestTabletTabBar tab={tab} onTab={(next) => setSearchParams(next === "idle" ? {} : { tab: next })} />
      {!canEdit ? <StammdatenWriteHint /> : null}

      <Card
        title="Vorschau"
        bodyClassName="p-0 overflow-hidden rounded-xl"
      >
        <div className="guest-tablet-root guest-tablet-idle-preview" data-kiosk="guest-tablet-preview">
          <div className="guest-tablet-shell !max-w-none !p-0">
            {idle.enabled ? (
              <TabletIdleScreen
                variant="welcome"
                idleScreen={idle}
                siteName={siteName}
                logoUrl={logoUrl}
                workstationLabel="Rezeption (Vorschau)"
              />
            ) : (
              <p className="p-8 text-center text-label-secondary">
                Leerlaufanzeige ist deaktiviert – es erscheint die Standardmeldung.
              </p>
            )}
          </div>
        </div>
        <p className="border-t border-separator px-4 py-2 text-xs text-label-secondary">
          <Link to="/guest-tablet/reception-1" className="text-accent underline" target="_blank">
            Tablet im Browser öffnen
          </Link>
        </p>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card title="Einstellungen">
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={idle.enabled}
                onChange={(e) => patchIdle({ enabled: e.target.checked })}
              />
              Leerlaufanzeige aktiv
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={idle.show_workstation_hint}
                onChange={(e) => patchIdle({ show_workstation_hint: e.target.checked })}
              />
              Arbeitsplatz-Hinweis unten anzeigen
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Wechselintervall Diashow (Sekunden)
              <input
                type="number"
                min={3}
                max={60}
                disabled={!canEdit}
                className="eh-input w-28"
                value={idle.slideshow_interval_seconds}
                onChange={(e) =>
                  patchIdle({
                    slideshow_interval_seconds: Math.min(60, Math.max(3, Number(e.target.value) || 8)),
                  })
                }
              />
            </label>
          </div>
        </Card>

        <Card title="Bilder für die Diashow">
          <div className="space-y-4">
            <p className="text-sm text-label-secondary">
              Bilder werden auf dem Tablet automatisch gewechselt. Empfohlen: Querformat, mind. 1280×720 px.
            </p>
            {idle.images.length > 0 ? (
              <ul className="grid gap-3 sm:grid-cols-2">
                {idle.images
                  .slice()
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((img) => (
                    <li key={img.id} className="space-y-2 rounded-lg border border-separator p-2">
                      <img
                        src={img.image_url}
                        alt={img.alt_text || ""}
                        className="aspect-video w-full rounded-md object-cover"
                      />
                      <label className="flex flex-col gap-1 text-xs">
                        Bildunterschrift
                        <input
                          disabled={!canEdit}
                          className="eh-input"
                          value={img.caption || ""}
                          onChange={(e) =>
                            patchIdle({
                              images: idle.images.map((i) =>
                                i.id === img.id ? { ...i, caption: e.target.value || null } : i,
                              ),
                            })
                          }
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-xs">
                        Alt-Text
                        <input
                          disabled={!canEdit}
                          className="eh-input"
                          value={img.alt_text || ""}
                          onChange={(e) =>
                            patchIdle({
                              images: idle.images.map((i) =>
                                i.id === img.id ? { ...i, alt_text: e.target.value || null } : i,
                              ),
                            })
                          }
                        />
                      </label>
                      {canEdit ? (
                        <Button type="button" variant="secondary" size="sm" onClick={() => removeImage(img.id)}>
                          Bild entfernen
                        </Button>
                      ) : null}
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-sm text-label-tertiary italic">Noch keine Bilder hinterlegt.</p>
            )}
            {canEdit ? (
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-accent">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  onChange={(e) => void onImageSelected(e.target.files?.[0] ?? null)}
                />
                Bild hinzufügen
              </label>
            ) : null}
          </div>
        </Card>
      </div>

      <Card title="Textfelder">
        <div className="space-y-4">
          <p className="text-sm text-label-secondary">
            Moderne Textkarten erscheinen neben der Diashow (oder zentriert, wenn keine Bilder vorhanden sind).
          </p>
          {idle.text_blocks
            .slice()
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((block) => (
              <div
                key={block.id}
                className="space-y-3 rounded-xl border border-separator bg-fill/30 p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="flex flex-col gap-1 text-sm">
                    Darstellung
                    <select
                      disabled={!canEdit}
                      className="eh-input w-44"
                      value={block.variant}
                      onChange={(e) =>
                        updateTextBlock(block.id, {
                          variant: e.target.value as GuestTabletIdleTextBlock["variant"],
                        })
                      }
                    >
                      {VARIANT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {canEdit ? (
                    <Button type="button" variant="secondary" size="sm" onClick={() => removeTextBlock(block.id)}>
                      Textfeld entfernen
                    </Button>
                  ) : null}
                </div>
                <label className="flex flex-col gap-1 text-sm">
                  Optionaler Titel
                  <input
                    disabled={!canEdit}
                    className="eh-input"
                    value={block.title || ""}
                    onChange={(e) => updateTextBlock(block.id, { title: e.target.value || null })}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  Text
                  <textarea
                    disabled={!canEdit}
                    className="eh-input min-h-[5rem]"
                    value={block.body}
                    onChange={(e) => updateTextBlock(block.id, { body: e.target.value })}
                  />
                </label>
              </div>
            ))}
          {canEdit ? (
            <Button type="button" variant="secondary" onClick={addTextBlock}>
              Textfeld hinzufügen
            </Button>
          ) : null}
        </div>
      </Card>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {success ? <p className="text-sm text-emerald-700">Gespeichert.</p> : null}

      {canEdit ? (
        <div className="flex gap-3">
          <Button type="button" onClick={() => void onSave()} disabled={saving}>
            {saving ? "Speichern…" : "Speichern"}
          </Button>
          <Button type="button" variant="secondary" onClick={() => void load()} disabled={saving}>
            Zurücksetzen
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function GuestTabletTabBar({
  tab,
  onTab,
}: {
  tab: GuestTabletTab;
  onTab: (tab: GuestTabletTab) => void;
}) {
  return (
    <div className="flex gap-1 rounded-xl border border-separator bg-grouped p-1">
      <button
        type="button"
        className={classNames(
          "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          tab === "idle" ? "bg-accent text-label-on-accent shadow-sm" : "text-label-secondary hover:bg-fill",
        )}
        onClick={() => onTab("idle")}
      >
        Leerlaufanzeige
      </button>
      <button
        type="button"
        className={classNames(
          "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          tab === "workstations"
            ? "bg-accent text-label-on-accent shadow-sm"
            : "text-label-secondary hover:bg-fill",
        )}
        onClick={() => onTab("workstations")}
      >
        Tablets / Arbeitsplätze
      </button>
    </div>
  );
}
